import { SFU_KINDS, type ServerOf } from '../../../../shared/protocol';

// 교실 송출 전담: Cloudflare Realtime SFU 세션 하나(RTCPeerConnection 하나)로 내 트랙을 올리고(push) 남의 트랙을 받는다(pull).
// SFU API는 게임 서버(Room)가 대신 부른다({t:'sfu', q, op, body} → {t:'sfu', q, ok, res|err}). 무엇을 올리고 받을지는 게임이 정한다(setSend·setRecv).
// 올린 트랙은 끄고 켤 때 replaceTrack만 쓴다(av.js와 같다). 실패(요청 실패·연결 끊김)하면 3초 뒤 새 세션으로 다시 하고,
// 두 번 실패하면 멈추고(dead) onDead를 한 번 부른다. stop()이 처음 상태로 되돌린다.
export const Sfu = (() => {
  const KIND = SFU_KINDS, RETRY_MS = 3000, CALL_MS = 10000, CONNECT_MS = 15000, MAX_FAIL = 2;
  const HARD = new Set(['stop', 'off']), SOFT = new Set(['class', 'rate', 'wait']);   // HARD: 다시 해도 안 됨(사용량 중지·비밀값 없음) → 바로 멈춤, SOFT: 잠깐 뒤엔 됨(교실 위치 전달 전·호출 수 제한·올린 쪽 연결 전) → 실패로 세지 않음
  let sendRaw = null, onTrack: any = () => {}, onDead: any = () => {}, ice = [];
  let pc = null, gen = 0, fails = 0, dead = false, busy = false, again = false, timer = 0, connT = 0, waitT = 0, qn = 0;
  let wantOut = {}, wantIn = [], inKey = '[]', announced = '', pubOn = {};
  const out = {};            // 올린 것: kind → { tr: 트랜시버, track: 지금 넣은 트랙 }
  const got = new Map();     // 받는 것: key → { id, kind, mid }
  const wait = new Map();    // 요청 번호 → { ok, no, t }
  const keyOf = r => r.id + ':' + r.kind + ':' + r.name;
  const err = (code, msg?) => Object.assign(new Error(msg || 'sfu ' + code), { code });

  function call(op, body?) {
    const q = ++qn;
    return new Promise<any>((ok, no) => {
      const t = setTimeout(() => { wait.delete(q); no(err('timeout', 'sfu timeout ' + op)); }, CALL_MS);
      wait.set(q, { ok, no, t });
      if (sendRaw) sendRaw(q, op, body || {});
    });
  }
  function onMsg(m: ServerOf<'sfu'>) {
    const w = wait.get(m.q); if (!w) return;
    wait.delete(m.q); clearTimeout(w.t);
    if (m.ok) w.ok(m.res || {}); else w.no(err(m.err, 'sfu ' + m.err));
  }

  function teardown() {
    gen++; clearTimeout(connT); clearTimeout(waitT);
    got.forEach(x => onTrack(x.id, x.kind, null)); got.clear();
    KIND.forEach(k => delete out[k]);
    if (pc) { pc.ontrack = pc.onconnectionstatechange = null; pc.close(); pc = null; }
    if (announced) { announced = ''; pubOn = {}; call('pub', {}).catch(() => {}); }   // Room의 내 송출 공지를 지운다
  }
  function fail(e) {
    if (dead) return;
    console.warn('[sfu]', e && e.message || e);
    teardown(); again = false;
    if (!SOFT.has(e && e.code)) fails++;
    if (HARD.has(e && e.code) || fails >= MAX_FAIL) { dead = true; onDead(); return; }
    clearTimeout(timer); timer = setTimeout(() => { timer = 0; kick(); }, RETRY_MS);
  }

  // 원하는 상태(wantOut·wantIn)에 맞춘다. 한 번에 하나만 돈다(협상이 겹치지 않게)
  async function step(g) {
    const live = () => { if (g !== gen) throw err('stale'); };
    if (!pc) {
      if (!KIND.some(k => wantOut[k]) && !wantIn.length) return;
      const p = pc = new RTCPeerConnection({ bundlePolicy: 'max-bundle', iceServers: ice });   // 직접 연결과 같은 STUN·TURN: UDP가 막힌 곳(학교·회사 방화벽)은 TURN을 거쳐 SFU에 닿는다
      p.ontrack = e => { for (const x of got.values()) if (x.mid === e.transceiver.mid) onTrack(x.id, x.kind, e.track); };
      p.onconnectionstatechange = () => {
        if (pc !== p) return;
        if (p.connectionState === 'failed') fail(err('ice', 'sfu ice failed'));
        else { if (p.connectionState === 'connected') clearTimeout(connT); kick(); }
      };
      connT = setTimeout(() => { if (pc === p && p.remoteDescription && p.connectionState !== 'connected') fail(err('ice', 'sfu connect timeout')); }, CONNECT_MS);
      await call('new'); live();
    }
    // 올리기: 처음 켠 종류만 트랜시버를 새로 만든다
    const add = KIND.filter(k => wantOut[k] && !out[k]);
    if (add.length) {
      const trs = add.map(k => pc.addTransceiver(wantOut[k], { direction: 'sendonly' }));
      add.forEach((k, i) => { out[k] = { tr: trs[i], track: wantOut[k] }; });
      await pc.setLocalDescription(await pc.createOffer()); live();
      const res = await call('push', { sdp: pc.localDescription.sdp, tracks: add.map((k, i) => ({ mid: trs[i].mid, kind: k })) }); live();
      await pc.setRemoteDescription(res.sessionDescription); live();
    }
    for (const k of KIND) if (out[k] && out[k].track !== (wantOut[k] || null)) { out[k].track = wantOut[k] || null; await out[k].tr.sender.replaceTrack(out[k].track); live(); }
    // 받기 닫기: 더 이상 공지되지 않은 트랙
    const keep = new Set(wantIn.map(keyOf)), drop = [...got].filter(([k]) => !keep.has(k));
    if (drop.length) {
      drop.forEach(([k, x]) => { got.delete(k); onTrack(x.id, x.kind, null); const t = pc.getTransceivers().find(t => t.mid === x.mid); if (t) t.direction = 'inactive'; });   // stop()은 BUNDLE 묶음을 바꿔 연결이 끊기니 inactive로 둔다
      await pc.setLocalDescription(await pc.createOffer()); live();
      const res = await call('close', { mids: drop.map(([, x]) => x.mid), sdp: pc.localDescription.sdp }); live();
      await pc.setRemoteDescription(res.sessionDescription); live();
    }
    // 받기: 새로 공지된 트랙. SFU가 offer를 주면 answer를 재협상으로 돌려준다
    const fresh = wantIn.filter(r => !got.has(keyOf(r)));
    if (fresh.length) {
      const res = await call('pull', { tracks: fresh.map(r => ({ id: r.id, kind: r.kind })) }); live();
      let later = false, bad = false;   // later: SFU가 아직 데이터를 못 받은 트랙(wait) → 세션은 두고 3초 뒤 그 트랙만 다시
      fresh.forEach(r => {
        const t = (res.tracks || []).find(t => t.trackName === r.name);
        if (t && t.mid && !t.err) got.set(keyOf(r), { id: r.id, kind: r.kind, mid: t.mid });
        else if (!t || t.err === 'wait') later = true; else bad = true;
      });
      if (res.requiresImmediateRenegotiation) {
        await pc.setRemoteDescription(res.sessionDescription); live();
        await pc.setLocalDescription(await pc.createAnswer()); live();
        await call('reneg', { sdp: pc.localDescription.sdp }); live();
      }
      if (bad) throw err('api', 'sfu pull failed');
      if (later) { clearTimeout(waitT); waitT = setTimeout(kick, RETRY_MS); }
    }
    // 송출 공지: rx = SFU로 받는 중, 종류 = 지금 트랙을 넣어 올리는 중. 연결 전에는 SFU가 트랙을 몰라 받는 쪽 pull이 실패하니 연결된 뒤에 알린다
    const on: any = {};
    if (pc.connectionState === 'connected') { on.rx = 1; KIND.forEach(k => { if (out[k] && out[k].track) on[k] = 1; }); }
    const s = JSON.stringify(on);
    if (s !== announced) { await call('pub', on); live(); announced = s; pubOn = on; }
  }
  function kick() {
    if (dead || timer || !sendRaw) return;
    if (busy) { again = true; return; }
    busy = true; const g = gen;
    (async () => {
      try { do { again = false; await step(g); } while (again && g === gen); }
      catch (e) { if (g === gen) fail(e); }
      finally { busy = false; if (again && !dead && !timer) kick(); }
    })();
  }

  return {
    // send(q, op, body): Room으로 요청 보내기, onTrack(id, kind, track|null): 받은 트랙이 생기거나 닫힘, onDead(): 두 번 실패해 멈춤, iceServers: /turn에서 받은 목록
    init({ send, onTrack: t, onDead: d, iceServers }) { sendRaw = send; onTrack = t || onTrack; onDead = d || onDead; ice = iceServers || []; kick(); },
    onMsg,
    // 올릴 트랙 { mic, cam, share } (없으면 올리지 않음)
    setSend(tracks) {
      const n = {}; KIND.forEach(k => { n[k] = tracks[k] || null; });
      if (KIND.some(k => n[k] !== (wantOut[k] || null))) { wantOut = n; kick(); }
    },
    // 받을 트랙 [{ id, kind, name }] (name = Room이 공지한 SFU 트랙 이름)
    setRecv(list) {
      const k = JSON.stringify(list.map(keyOf).sort());
      if (k !== inKey) { inKey = k; wantIn = list.slice(); kick(); }
    },
    // 이 종류를 SFU로 올리고 있고 세션이 연결됨 (이때만 직접 연결로 보내지 않아도 된다)
    sending: kind => !!(pc && pc.connectionState === 'connected' && pubOn[kind]),
    get dead() { return dead; },
    get active() { return !!(pc || busy || timer || dead || announced); },
    stop() { wantOut = {}; wantIn = []; inKey = '[]'; teardown(); clearTimeout(timer); timer = 0; fails = 0; dead = false; },
    peerConn: () => pc,   // 시험용
    status: () => ({ state: pc ? pc.connectionState : 'none', fails, dead, out: KIND.filter(k => out[k] && out[k].track), got: [...got.keys()] }),
  };
})();
